import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { contractApi } from "../../../../modules/contract/api";
import {
  ChangeContractVerificationStatus,
  ContractFilterExportRequest,
  ContractFilterPageRequest,
  ContractVehiclesExportRequest,
  CreateUpdateContract,
  UpdateContractAttachment,
  UpdateContractComplaintNote,
  UpdateContractEndDates,
  UpdateContractGainers,
} from "../../../../modules/contract/types";
import { UUID } from "../../../../typings/global";
import { ExportFileType } from "../../../enums";
import {
  EntityObject,
  OptimisticLockVersion,
  PageRequest,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../../types";

export const CONTRACTS_QUERY_KEYS = {
  filterContracts: (payload?: ContractFilterPageRequest) =>
    payload ? ["filterContracts", payload] : ["filterContracts"],
  downloadContractsExport: () => ["downloadContractsExport"],
  downloadContractsVerificationExport: () => ["downloadContractsVerificationExport"],
  changeContractVerificationStatus: () => ["changeContractVerificationStatus"],
  toggleVerificationExportInclusion: () => ["toggleVerificationExportInclusion"],
  getContract: (id: UUID) => ["getContract", id],
  createContract: () => ["createContract"],
  updateContract: () => ["updateContract"],
  updateContractEndDates: () => ["updateContractEndDates"],
  deleteContract: () => ["deleteContract"],
  updateContractComplaintNote: () => ["updateComplaintNote"],
  uploadContractAttachments: () => ["uploadContractAttachments"],
  updateContractAttachment: () => ["updateContractAttachment"],
  deleteContractAttachment: () => ["deleteContractAttachment"],
  getContractExternalView: () => ["getContractExternalView"],
  filterContractCommissions: (payload: EntityObject<PageRequest>) => ["filterContractCommissions", payload],
  importContracts: () => ["importContracts"],
  downloadContractAttachmentsAsZip: () => ["downloadContractAttachmentsAsZip"],
  downloadContractVehiclesExport: () => ["downloadContractVehiclesExport"],
  downloadContractCommissionsExport: () => ["downloadContractCommissionsExport"],
  updateContractGainers: () => ["updateContractGainers"],
};

export const useFilterContracts = (payload?: ContractFilterPageRequest) => {
  return useQuery({
    queryKey: CONTRACTS_QUERY_KEYS.filterContracts(payload),
    queryFn: async () => {
      const { data } = await contractApi.filterContracts(payload!);

      return data;
    },
    enabled: !!payload,
    placeholderData: keepPreviousData,
  });
};

export const useDownloadContractsExport = () => {
  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.downloadContractsExport(),
    mutationFn: async (payload: ContractFilterExportRequest) => {
      const response = await contractApi.downloadContractsExport(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useDownloadContractsVerificationExport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.downloadContractsVerificationExport(),
    mutationFn: async () => {
      const response = await contractApi.downloadContractsVerificationExport();
      openBlobFile(response, true);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.filterContracts() });
    },
  });
};

export const useChangeContractVerificationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.changeContractVerificationStatus(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<ChangeContractVerificationStatus>) => {
      const { data } = await contractApi.changeContractVerificationStatus(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      Notification.itemUpdatedNotification();
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id) });
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.filterContracts() });
    },
  });
};

export const useToggleVerificationExportInclusion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.toggleVerificationExportInclusion(),
    mutationFn: async (payload: EntityObject<OptimisticLockVersion>) => {
      const { data } = await contractApi.toggleVerificationExportInclusion(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      Notification.itemUpdatedNotification();
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id) });
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.filterContracts() });
    },
  });
};

export const useGetContract = (id: UUID) => {
  return useQuery({
    queryKey: CONTRACTS_QUERY_KEYS.getContract(id),
    queryFn: async () => {
      const { data } = await contractApi.getContract({ id });

      return data;
    },
    enabled: !!id,
    retry: 1,
  });
};

export const useCreateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.createContract(),
    mutationFn: async (payload: CreateUpdateContract) => {
      const { data } = await contractApi.createContract(payload);

      return data;
    },
    onSuccess: () => {
      Notification.itemCreatedNotification();
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.filterContracts() });
    },
  });
};

export const useUpdateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.updateContract(),
    mutationFn: async (payload: EntityObject<CreateUpdateContract>) => {
      const { data } = await contractApi.updateContract(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      Notification.itemUpdatedNotification();
      queryClient.setQueryData(CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id), data);
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.filterContracts() });
    },
  });
};

export const useUpdateContractEndDates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.updateContractEndDates(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UpdateContractEndDates>) => {
      const { data } = await contractApi.updateContractEndDates(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      Notification.itemUpdatedNotification();
      queryClient.setQueryData(CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id), data);
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.filterContracts() });
    },
  });
};

export const useDeleteContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.deleteContract(),
    mutationFn: (id: UUID) => {
      return contractApi.deleteContract({ id });
    },
    onSuccess: () => {
      Notification.itemDeletedNotification();
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.filterContracts() });
    },
  });
};

export const useUpdateContractComplaintNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.updateContractComplaintNote(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UpdateContractComplaintNote>) => {
      const { data } = await contractApi.updateComplaintNote(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      Notification.itemUpdatedNotification();
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.filterContracts() });
      queryClient.setQueryData(CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id), data);
    },
  });
};

export const useUploadContractAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.uploadContractAttachments(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await contractApi.uploadContractAttachments(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id) });
    },
  });
};

export const useUpdateContractAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.updateContractAttachment(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<UpdateContractAttachment>) => {
      const { data } = await contractApi.updateContractAttachment(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id1) });
    },
  });
};

export const useDeleteContractAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.deleteContractAttachment(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return contractApi.deleteContractAttachment(payload);
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.invalidateQueries({ queryKey: CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id1) });
    },
  });
};

export const useGetContractExternalView = () => {
  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.getContractExternalView(),
    meta: { globalSpinner: false },
    mutationFn: async (id: UUID) => {
      const { data } = await contractApi.getContractExternalView({ id });

      return data;
    },
  });
};

export const useFilterContractCommissions = (payload: EntityObject<PageRequest>) => {
  return useQuery({
    queryKey: CONTRACTS_QUERY_KEYS.filterContractCommissions(payload),
    queryFn: async () => {
      const { data } = await contractApi.filterContractCommissions(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useImportContracts = () => {
  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.importContracts(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: FormData) => {
      const { data } = await contractApi.importContracts(payload);

      return data;
    },
    onSuccess: (data) => {
      Notification.getNotification().success({
        title: t("common.operationSuccess"),
        description: t("contract.helpers.import.processStarted", { count: data }),
      });
    },
  });
};

export const useDownloadContractAttachmentsAsZip = () => {
  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.downloadContractAttachmentsAsZip(),
    mutationFn: async (id: UUID) => {
      const response = await contractApi.downloadContractAttachmentsAsZip({ id });
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useDownloadContractVehiclesExport = () => {
  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.downloadContractVehiclesExport(),
    mutationFn: async (payload: EntityObject<ContractVehiclesExportRequest>) => {
      const response = await contractApi.downloadContractVehiclesExport(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useDownloadContractCommissionsExport = () => {
  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.downloadContractCommissionsExport(),
    mutationFn: async (payload: EntityObject<{ exportFileType: ExportFileType }>) => {
      const response = await contractApi.downloadContractCommissionsExport(payload);
      openBlobFile(response, true);

      return response.data;
    },
  });
};

export const useUpdateContractGainers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CONTRACTS_QUERY_KEYS.updateContractGainers(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<UpdateContractGainers>) => {
      const { data } = await contractApi.updateContractGainers(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(CONTRACTS_QUERY_KEYS.getContract(mutatedPayload.id), data);
      Notification.itemUpdatedNotification();
    },
  });
};
